import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function BankAccountManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("types");
  const [loading, setLoading] = useState(true);

  // Data states
  const [accountTypes, setAccountTypes] = useState([]);
  const [bankSettings, setBankSettings] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Modal states
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showSettingModal, setShowSettingModal] = useState(false);
  const [showOpenAccountModal, setShowOpenAccountModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);

  // Form states
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [typeForm, setTypeForm] = useState({
    classCode: "", name: "", shortCode: "", description: "",
    minimumBalance: 0, monthlyFee: 0, interestRate: 0,
    dailyTransactionLimit: 1000000, maximumBalance: 50000000,
    availableForAgents: false, isCorporate: false,
    requiresKyc: true, minimumKycTier: 1, glAccountCode: ""
  });
  const [settingForm, setSettingForm] = useState({ value: "", description: "" });
  const [openAccountForm, setOpenAccountForm] = useState({
    userId: "", accountTypeId: "", branchId: "", initialDeposit: 0
  });
  const [validateInput, setValidateInput] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [processing, setProcessing] = useState(false);

  // Load data
  const loadAccountTypes = useCallback(async () => {
    try {
      const res = await api.get("/BankAccount/types");
      setAccountTypes(res.data);
    } catch (err) {
      console.error("Failed to load account types:", err);
    }
  }, []);

  const loadBankSettings = useCallback(async () => {
    try {
      const res = await api.get("/BankAccount/settings");
      setBankSettings(res.data);
    } catch (err) {
      console.error("Failed to load bank settings:", err);
    }
  }, []);

  const loadSequences = useCallback(async () => {
    try {
      const res = await api.get("/BankAccount/sequences");
      setSequences(res.data);
    } catch (err) {
      console.error("Failed to load sequences:", err);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get("/Branch");
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to load branches:", err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadAccountTypes(), loadBankSettings(), loadSequences(), loadBranches()]);
      setLoading(false);
    };
    loadData();
  }, [loadAccountTypes, loadBankSettings, loadSequences, loadBranches]);

  // Search customers
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await api.get(`/AccountOpening/search?query=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  // Handlers
  const handleInitializeTypes = async () => {
    if (!window.confirm("Initialize default account types? This will only work if no types exist.")) return;
    setProcessing(true);
    try {
      const res = await api.post("/BankAccount/types/initialize");
      alert(res.data.message);
      loadAccountTypes();
      loadBankSettings();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to initialize");
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      if (selectedType) {
        await api.put(`/BankAccount/types/${selectedType.id}`, typeForm);
        alert("Account type updated!");
      } else {
        await api.post("/BankAccount/types", typeForm);
        alert("Account type created!");
      }
      setShowTypeModal(false);
      resetTypeForm();
      loadAccountTypes();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save account type");
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSetting = async (e) => {
    e.preventDefault();
    if (!selectedSetting) return;
    setProcessing(true);
    try {
      await api.put(`/BankAccount/settings/${selectedSetting.settingKey}`, settingForm);
      alert("Setting updated!");
      setShowSettingModal(false);
      loadBankSettings();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update setting");
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenAccount = async (e) => {
    e.preventDefault();
    if (!openAccountForm.userId || !openAccountForm.accountTypeId || !openAccountForm.branchId) {
      alert("Please fill in all required fields");
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post("/BankAccount/open", {
        userId: parseInt(openAccountForm.userId),
        accountTypeId: parseInt(openAccountForm.accountTypeId),
        branchId: parseInt(openAccountForm.branchId),
        initialDeposit: parseFloat(openAccountForm.initialDeposit) || 0
      });
      alert(`✅ Account opened!\n\nAccount Number: ${res.data.account.accountNumber}\nAccount Name: ${res.data.account.accountName}\nType: ${res.data.account.accountType}\nBranch: ${res.data.account.branch}`);
      setShowOpenAccountModal(false);
      setOpenAccountForm({ userId: "", accountTypeId: "", branchId: "", initialDeposit: 0 });
      setSearchResults([]);
      setCustomerSearch("");
      loadSequences();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to open account");
    } finally {
      setProcessing(false);
    }
  };

  const handleValidateAccount = async () => {
    if (!validateInput || validateInput.length !== 10) {
      alert("Please enter a 10-digit account number");
      return;
    }
    try {
      const res = await api.get(`/BankAccount/validate/${validateInput}`);
      setValidationResult(res.data);
    } catch (err) {
      alert("Validation failed");
    }
  };

  const resetTypeForm = () => {
    setSelectedType(null);
    setTypeForm({
      classCode: "", name: "", shortCode: "", description: "",
      minimumBalance: 0, monthlyFee: 0, interestRate: 0,
      dailyTransactionLimit: 1000000, maximumBalance: 50000000,
      availableForAgents: false, isCorporate: false,
      requiresKyc: true, minimumKycTier: 1, glAccountCode: ""
    });
  };

  const openEditTypeModal = (type) => {
    setSelectedType(type);
    setTypeForm({
      classCode: type.classCode,
      name: type.name,
      shortCode: type.shortCode,
      description: type.description || "",
      minimumBalance: type.minimumBalance,
      monthlyFee: type.monthlyFee,
      interestRate: type.interestRate,
      dailyTransactionLimit: type.dailyTransactionLimit,
      maximumBalance: type.maximumBalance,
      availableForAgents: type.availableForAgents,
      isCorporate: type.isCorporate,
      requiresKyc: type.requiresKyc,
      minimumKycTier: type.minimumKycTier,
      glAccountCode: type.glAccountCode || ""
    });
    setShowTypeModal(true);
  };

  const openEditSettingModal = (setting) => {
    setSelectedSetting(setting);
    setSettingForm({
      value: setting.settingValue,
      description: setting.description || ""
    });
    setShowSettingModal(true);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;

  const getBankCode = () => {
    const setting = bankSettings.find(s => s.settingKey === "BankCode");
    return setting?.settingValue || "309";
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading Bank Account Management...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>🏦 Bank Account Management</h1>
        <div style={styles.headerActions}>
          <button onClick={() => setShowValidateModal(true)} style={styles.validateBtn}>
            ✓ Validate NUBAN
          </button>
          <button onClick={() => setShowOpenAccountModal(true)} style={styles.openBtn}>
            + Open Account
          </button>
        </div>
      </div>

      {/* Bank Code Display */}
      <div style={styles.bankCodeCard}>
        <div style={styles.bankCodeIcon}>🏛️</div>
        <div>
          <div style={styles.bankCodeLabel}>Bank Code (NUBAN)</div>
          <div style={styles.bankCodeValue}>{getBankCode()}</div>
        </div>
        <div style={styles.bankCodeInfo}>
          Account format: <code>{getBankCode()}TTBBSS[C]</code>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button 
          onClick={() => setActiveTab("types")} 
          style={{ ...styles.tab, ...(activeTab === "types" ? styles.tabActive : {}) }}
        >
          📋 Account Types
        </button>
        <button 
          onClick={() => setActiveTab("settings")} 
          style={{ ...styles.tab, ...(activeTab === "settings" ? styles.tabActive : {}) }}
        >
          ⚙️ Bank Settings
        </button>
        <button 
          onClick={() => setActiveTab("sequences")} 
          style={{ ...styles.tab, ...(activeTab === "sequences" ? styles.tabActive : {}) }}
        >
          🔢 Branch Sequences
        </button>
      </div>

      {/* Account Types Tab */}
      {activeTab === "types" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}>
            <h3>Account Types ({accountTypes.length})</h3>
            <div style={{ display: "flex", gap: "10px" }}>
              {accountTypes.length === 0 && (
                <button onClick={handleInitializeTypes} style={styles.initBtn} disabled={processing}>
                  🚀 Initialize Defaults
                </button>
              )}
              <button onClick={() => { resetTypeForm(); setShowTypeModal(true); }} style={styles.addBtn}>
                + Add Type
              </button>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Min Balance</th>
                  <th style={styles.th}>Interest</th>
                  <th style={styles.th}>Daily Limit</th>
                  <th style={styles.th}>KYC Tier</th>
                  <th style={styles.th}>Features</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accountTypes.map(type => (
                  <tr key={type.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.classCode}>{type.classCode}</span>
                      <span style={styles.shortCode}>{type.shortCode}</span>
                    </td>
                    <td style={styles.td}>
                      <strong>{type.name}</strong>
                      {type.description && (
                        <div style={styles.description}>{type.description}</div>
                      )}
                    </td>
                    <td style={styles.td}>{formatCurrency(type.minimumBalance)}</td>
                    <td style={styles.td}>{type.interestRate}%</td>
                    <td style={styles.td}>{formatCurrency(type.dailyTransactionLimit)}</td>
                    <td style={styles.td}>
                      <span style={styles.tierBadge}>Tier {type.minimumKycTier}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.features}>
                        {type.availableForAgents && <span style={styles.featureBadge}>Agent</span>}
                        {type.isCorporate && <span style={styles.featureBadge}>Corporate</span>}
                        {type.requiresKyc && <span style={styles.kycBadge}>KYC</span>}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => openEditTypeModal(type)} style={styles.editBtn}>
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bank Settings Tab */}
      {activeTab === "settings" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}>
            <h3>Bank Settings ({bankSettings.length})</h3>
          </div>

          <div style={styles.settingsGrid}>
            {bankSettings.map(setting => (
              <div key={setting.id} style={styles.settingCard}>
                <div style={styles.settingHeader}>
                  <span style={styles.settingKey}>{setting.settingKey}</span>
                  {setting.isEditable && (
                    <button onClick={() => openEditSettingModal(setting)} style={styles.editBtnSmall}>
                      ✏️
                    </button>
                  )}
                </div>
                <div style={styles.settingValue}>{setting.settingValue}</div>
                {setting.description && (
                  <div style={styles.settingDesc}>{setting.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sequences Tab */}
      {activeTab === "sequences" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}>
            <h3>Branch Account Sequences</h3>
            <button onClick={loadSequences} style={styles.refreshBtn}>🔄 Refresh</button>
          </div>

          {sequences.length > 0 ? (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Branch</th>
                    <th style={styles.th}>Account Type</th>
                    <th style={styles.th}>Last Sequence</th>
                    <th style={styles.th}>Capacity</th>
                    <th style={styles.th}>Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {sequences.map(seq => (
                    <tr key={seq.id} style={styles.tr}>
                      <td style={styles.td}>
                        <strong>{seq.branchName}</strong>
                        <div style={styles.branchCode}>{seq.branchCode}</div>
                      </td>
                      <td style={styles.td}>
                        {seq.accountTypeName}
                        <span style={styles.typeCode}>{seq.accountTypeCode}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.sequenceNum}>{seq.lastSequence}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.capacityBar}>
                          <div 
                            style={{
                              ...styles.capacityFill,
                              width: `${(seq.lastSequence / seq.maxSequence) * 100}%`,
                              backgroundColor: seq.remainingCapacity < 1000 ? "#ef4444" : "#10b981"
                            }}
                          />
                        </div>
                        <span style={styles.capacityText}>
                          {seq.remainingCapacity.toLocaleString()} remaining
                        </span>
                      </td>
                      <td style={styles.td}>
                        {seq.lastUsedAt ? new Date(seq.lastUsedAt).toLocaleString() : "Never"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <p>No sequences created yet. Sequences are created automatically when accounts are opened.</p>
            </div>
          )}
        </div>
      )}

      {/* Account Type Modal */}
      {showTypeModal && (
        <div style={styles.modalOverlay} onClick={() => setShowTypeModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {selectedType ? "Edit Account Type" : "Create Account Type"}
            </h3>
            <form onSubmit={handleSaveType}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Class Code (2 digits) *</label>
                  <input
                    type="text"
                    maxLength="2"
                    value={typeForm.classCode}
                    onChange={e => setTypeForm({ ...typeForm, classCode: e.target.value })}
                    style={styles.input}
                    placeholder="e.g., 10"
                    required
                    disabled={!!selectedType}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Short Code *</label>
                  <input
                    type="text"
                    maxLength="5"
                    value={typeForm.shortCode}
                    onChange={e => setTypeForm({ ...typeForm, shortCode: e.target.value.toUpperCase() })}
                    style={styles.input}
                    placeholder="e.g., SAV"
                    required
                  />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                  <label>Name *</label>
                  <input
                    type="text"
                    value={typeForm.name}
                    onChange={e => setTypeForm({ ...typeForm, name: e.target.value })}
                    style={styles.input}
                    placeholder="e.g., Savings Account"
                    required
                  />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                  <label>Description</label>
                  <textarea
                    value={typeForm.description}
                    onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
                    style={styles.textarea}
                    placeholder="Description of this account type"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Minimum Balance (₦)</label>
                  <input
                    type="number"
                    value={typeForm.minimumBalance}
                    onChange={e => setTypeForm({ ...typeForm, minimumBalance: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Monthly Fee (₦)</label>
                  <input
                    type="number"
                    value={typeForm.monthlyFee}
                    onChange={e => setTypeForm({ ...typeForm, monthlyFee: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={typeForm.interestRate}
                    onChange={e => setTypeForm({ ...typeForm, interestRate: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Daily Transaction Limit (₦)</label>
                  <input
                    type="number"
                    value={typeForm.dailyTransactionLimit}
                    onChange={e => setTypeForm({ ...typeForm, dailyTransactionLimit: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Maximum Balance (₦)</label>
                  <input
                    type="number"
                    value={typeForm.maximumBalance}
                    onChange={e => setTypeForm({ ...typeForm, maximumBalance: parseFloat(e.target.value) || 0 })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Minimum KYC Tier</label>
                  <select
                    value={typeForm.minimumKycTier}
                    onChange={e => setTypeForm({ ...typeForm, minimumKycTier: parseInt(e.target.value) })}
                    style={styles.input}
                  >
                    <option value={0}>Tier 0 (None)</option>
                    <option value={1}>Tier 1 (Basic)</option>
                    <option value={2}>Tier 2 (Standard)</option>
                    <option value={3}>Tier 3 (Enhanced)</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>GL Account Code</label>
                  <input
                    type="text"
                    value={typeForm.glAccountCode}
                    onChange={e => setTypeForm({ ...typeForm, glAccountCode: e.target.value })}
                    style={styles.input}
                    placeholder="e.g., 2100"
                  />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                  <div style={styles.checkboxGroup}>
                    <label style={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={typeForm.requiresKyc}
                        onChange={e => setTypeForm({ ...typeForm, requiresKyc: e.target.checked })}
                      />
                      Requires KYC
                    </label>
                    <label style={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={typeForm.availableForAgents}
                        onChange={e => setTypeForm({ ...typeForm, availableForAgents: e.target.checked })}
                      />
                      Available for Agents
                    </label>
                    <label style={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={typeForm.isCorporate}
                        onChange={e => setTypeForm({ ...typeForm, isCorporate: e.target.checked })}
                      />
                      Corporate Account
                    </label>
                  </div>
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowTypeModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn} disabled={processing}>
                  {processing ? "Saving..." : (selectedType ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Setting Edit Modal */}
      {showSettingModal && selectedSetting && (
        <div style={styles.modalOverlay} onClick={() => setShowSettingModal(false)}>
          <div style={{ ...styles.modal, maxWidth: "400px" }} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Edit Setting: {selectedSetting.settingKey}</h3>
            <form onSubmit={handleSaveSetting}>
              <div style={styles.formGroup}>
                <label>Value *</label>
                <input
                  type="text"
                  value={settingForm.value}
                  onChange={e => setSettingForm({ ...settingForm, value: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={settingForm.description}
                  onChange={e => setSettingForm({ ...settingForm, description: e.target.value })}
                  style={styles.textarea}
                />
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowSettingModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn} disabled={processing}>
                  {processing ? "Saving..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Open Account Modal */}
      {showOpenAccountModal && (
        <div style={styles.modalOverlay} onClick={() => setShowOpenAccountModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>🏦 Open New Account</h3>
            <form onSubmit={handleOpenAccount}>
              {/* Customer Search */}
              <div style={styles.formGroup}>
                <label>Search Customer *</label>
                <input
                  type="text"
                  value={customerSearch}
                  onChange={e => {
                    setCustomerSearch(e.target.value);
                    searchCustomers(e.target.value);
                  }}
                  style={styles.input}
                  placeholder="Search by name, email, or account number..."
                />
                {searchResults.length > 0 && (
                  <div style={styles.searchResults}>
                    {searchResults.map(customer => (
                      <div
                        key={customer.id}
                        style={styles.searchResultItem}
                        onClick={() => {
                          setOpenAccountForm({ ...openAccountForm, userId: customer.id });
                          setCustomerSearch(customer.fullName);
                          setSearchResults([]);
                        }}
                      >
                        <strong>{customer.fullName}</strong>
                        <span style={styles.customerEmail}>{customer.email}</span>
                      </div>
                    ))}
                  </div>
                )}
                {openAccountForm.userId && (
                  <div style={styles.selectedCustomer}>
                    ✓ Customer ID: {openAccountForm.userId}
                  </div>
                )}
              </div>

              {/* Account Type */}
              <div style={styles.formGroup}>
                <label>Account Type *</label>
                <select
                  value={openAccountForm.accountTypeId}
                  onChange={e => setOpenAccountForm({ ...openAccountForm, accountTypeId: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select account type...</option>
                  {accountTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.classCode} - {type.name} (Min: {formatCurrency(type.minimumBalance)})
                    </option>
                  ))}
                </select>
              </div>

              {/* Branch */}
              <div style={styles.formGroup}>
                <label>Branch *</label>
                <select
                  value={openAccountForm.branchId}
                  onChange={e => setOpenAccountForm({ ...openAccountForm, branchId: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select branch...</option>
                  {branches.filter(b => b.isActive).map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Initial Deposit */}
              <div style={styles.formGroup}>
                <label>Initial Deposit (₦)</label>
                <input
                  type="number"
                  value={openAccountForm.initialDeposit}
                  onChange={e => setOpenAccountForm({ ...openAccountForm, initialDeposit: parseFloat(e.target.value) || 0 })}
                  style={styles.input}
                  placeholder="0"
                />
              </div>

              {/* Account Number Preview */}
              {openAccountForm.accountTypeId && openAccountForm.branchId && (
                <div style={styles.previewBox}>
                  <div style={styles.previewLabel}>Account Number Format Preview:</div>
                  <div style={styles.previewNumber}>
                    {getBankCode()}
                    {accountTypes.find(t => t.id === parseInt(openAccountForm.accountTypeId))?.classCode || "XX"}
                    {branches.find(b => b.id === parseInt(openAccountForm.branchId))?.code?.substring(0, 2) || "XX"}
                    XX[C]
                  </div>
                </div>
              )}

              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowOpenAccountModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn} disabled={processing}>
                  {processing ? "Opening..." : "Open Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Validate NUBAN Modal */}
      {showValidateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowValidateModal(false)}>
          <div style={{ ...styles.modal, maxWidth: "450px" }} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>✓ Validate NUBAN Account Number</h3>
            <div style={styles.formGroup}>
              <label>Account Number (10 digits)</label>
              <input
                type="text"
                maxLength="10"
                value={validateInput}
                onChange={e => {
                  setValidateInput(e.target.value.replace(/\D/g, ""));
                  setValidationResult(null);
                }}
                style={{ ...styles.input, fontSize: "24px", textAlign: "center", letterSpacing: "4px" }}
                placeholder="0000000000"
              />
            </div>
            <button 
              type="button" 
              onClick={handleValidateAccount} 
              style={{ ...styles.submitBtn, width: "100%", marginBottom: "15px" }}
            >
              Validate
            </button>

            {validationResult && (
              <div style={{
                ...styles.validationResult,
                backgroundColor: validationResult.isValid ? "#d1fae5" : "#fee2e2",
                borderColor: validationResult.isValid ? "#10b981" : "#ef4444"
              }}>
                <div style={styles.validationStatus}>
                  {validationResult.isValid ? "✅ Valid NUBAN" : "❌ Invalid NUBAN"}
                </div>
                {validationResult.isValid && validationResult.components && (
                  <div style={styles.validationDetails}>
                    <div><strong>Bank Code:</strong> {validationResult.components.bankCode}</div>
                    <div><strong>Account Type:</strong> {validationResult.components.accountTypeCode}</div>
                    <div><strong>Branch:</strong> {validationResult.components.branchCode}</div>
                    <div><strong>Sequence:</strong> {validationResult.components.sequenceNumber}</div>
                    <div><strong>Check Digit:</strong> {validationResult.components.checkDigit}</div>
                  </div>
                )}
              </div>
            )}

            <div style={styles.modalFooter}>
              <button onClick={() => setShowValidateModal(false)} style={styles.cancelBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px"
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1a1a2e",
    margin: 0
  },
  backBtn: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  headerActions: {
    display: "flex",
    gap: "10px"
  },
  validateBtn: {
    padding: "10px 20px",
    backgroundColor: "#8b5cf6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600"
  },
  openBtn: {
    padding: "10px 20px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600"
  },
  bankCodeCard: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    backgroundColor: "#1e3a5f",
    color: "white",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px"
  },
  bankCodeIcon: {
    fontSize: "40px"
  },
  bankCodeLabel: {
    fontSize: "12px",
    opacity: 0.8
  },
  bankCodeValue: {
    fontSize: "32px",
    fontWeight: "bold",
    fontFamily: "monospace"
  },
  bankCodeInfo: {
    marginLeft: "auto",
    fontSize: "14px",
    opacity: 0.8
  },
  tabs: {
    display: "flex",
    gap: "5px",
    marginBottom: "20px",
    backgroundColor: "white",
    padding: "5px",
    borderRadius: "12px",
    flexWrap: "wrap"
  },
  tab: {
    padding: "10px 20px",
    backgroundColor: "transparent",
    color: "#6b7280",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500"
  },
  tabActive: {
    backgroundColor: "#10b981",
    color: "white"
  },
  content: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  initBtn: {
    padding: "10px 20px",
    backgroundColor: "#f59e0b",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600"
  },
  addBtn: {
    padding: "10px 20px",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600"
  },
  refreshBtn: {
    padding: "8px 16px",
    backgroundColor: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  tableContainer: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px"
  },
  th: {
    textAlign: "left",
    padding: "12px",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase"
  },
  tr: {
    borderBottom: "1px solid #e5e7eb"
  },
  td: {
    padding: "12px",
    fontSize: "14px",
    color: "#374151"
  },
  classCode: {
    display: "inline-block",
    backgroundColor: "#1e3a5f",
    color: "white",
    padding: "4px 8px",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontWeight: "bold",
    marginRight: "8px"
  },
  shortCode: {
    color: "#6b7280",
    fontSize: "12px"
  },
  description: {
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "4px"
  },
  tierBadge: {
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600"
  },
  features: {
    display: "flex",
    gap: "5px",
    flexWrap: "wrap"
  },
  featureBadge: {
    backgroundColor: "#fef3c7",
    color: "#92400e",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "10px"
  },
  kycBadge: {
    backgroundColor: "#d1fae5",
    color: "#065f46",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "10px"
  },
  editBtn: {
    padding: "6px 12px",
    backgroundColor: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px"
  },
  editBtnSmall: {
    padding: "4px 8px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "14px"
  },
  settingsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "15px"
  },
  settingCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    padding: "15px"
  },
  settingHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px"
  },
  settingKey: {
    fontSize: "12px",
    color: "#6b7280",
    textTransform: "uppercase",
    fontWeight: "600"
  },
  settingValue: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#1f2937"
  },
  settingDesc: {
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "8px"
  },
  branchCode: {
    fontSize: "12px",
    color: "#6b7280"
  },
  typeCode: {
    marginLeft: "8px",
    backgroundColor: "#e5e7eb",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "11px"
  },
  sequenceNum: {
    fontFamily: "monospace",
    fontSize: "16px",
    fontWeight: "bold"
  },
  capacityBar: {
    height: "8px",
    backgroundColor: "#e5e7eb",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "4px"
  },
  capacityFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s"
  },
  capacityText: {
    fontSize: "11px",
    color: "#6b7280"
  },
  emptyState: {
    textAlign: "center",
    padding: "40px",
    color: "#6b7280"
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: "20px"
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "25px",
    width: "100%",
    maxWidth: "600px",
    maxHeight: "90vh",
    overflow: "auto"
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "20px",
    color: "#1f2937"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "15px"
  },
  formGroup: {
    marginBottom: "15px"
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    minHeight: "80px",
    resize: "vertical",
    boxSizing: "border-box"
  },
  checkboxGroup: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap"
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "15px",
    borderTop: "1px solid #e5e7eb"
  },
  cancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  submitBtn: {
    padding: "10px 20px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600"
  },
  searchResults: {
    position: "absolute",
    backgroundColor: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    maxHeight: "200px",
    overflow: "auto",
    zIndex: 10,
    width: "calc(100% - 50px)"
  },
  searchResultItem: {
    padding: "10px 15px",
    cursor: "pointer",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  customerEmail: {
    fontSize: "12px",
    color: "#6b7280"
  },
  selectedCustomer: {
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "#d1fae5",
    color: "#065f46",
    borderRadius: "6px",
    fontSize: "13px"
  },
  previewBox: {
    backgroundColor: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    padding: "15px",
    marginTop: "10px"
  },
  previewLabel: {
    fontSize: "12px",
    color: "#0369a1",
    marginBottom: "5px"
  },
  previewNumber: {
    fontSize: "24px",
    fontFamily: "monospace",
    fontWeight: "bold",
    color: "#0c4a6e",
    letterSpacing: "2px"
  },
  validationResult: {
    padding: "15px",
    borderRadius: "8px",
    border: "2px solid"
  },
  validationStatus: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "10px"
  },
  validationDetails: {
    fontSize: "14px",
    lineHeight: "1.8"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    fontSize: "16px",
    color: "#6b7280"
  }
};
